<template>
  <div class="medium-ca-decrypt-container">
    <div class="status-section mb-6">
      <div class="flex items-center justify-center">
        <div class="text-center">
          <div class="text-lg font-semibold mb-2" :class="statusColor">
            {{ statusText }}
          </div>
        </div>
      </div>
    </div>
    <div class="main-section">
      <div v-if="currentStatus === CA_STATUS.WAITING" class="waiting-section">
        <div class="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
          <div class="text-6xl mb-4">🔑</div>
          <h3 class="text-xl font-semibold text-gray-800 mb-2">请插入CA介质</h3>
          <p class="text-gray-600 mb-6">请将USB Key等CA解密介质插入电脑的USB接口</p>
          <div class="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 text-left">
            <div class="flex">
              <a-icon type="info-circle" class="text-blue-500 text-lg mr-2 mt-0.5" />
              <div>
                <p class="text-blue-800 font-medium">操作说明：</p>
                <ul class="text-blue-600 text-sm mt-1 space-y-1">
                  <li>1. 确认已安装CA驱动程序</li>
                  <li>2. 将USB Key插入电脑USB接口</li>
                  <li>3. 点击下方"开始解密"按钮</li>
                </ul>
              </div>
            </div>
          </div>
          <div class="flex justify-center space-x-4">
            <a-button @click="handleBack">上一步</a-button>
            <a-button type="primary" size="large" @click="startDecrypt">
              <a-icon type="safety-certificate" class="mr-1" />开始解密
            </a-button>
          </div>
        </div>
      </div>

      <div v-if="currentStatus === CA_STATUS.DECRYPTING" class="processing-section">
        <div class="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
          <div class="text-6xl mb-4">
            <div class="animate-spin inline-block">⚙️</div>
          </div>
          <h3 class="text-xl font-semibold text-gray-800 mb-2">正在解密...</h3>
          <p class="text-gray-600 mb-6">解密进行中，请耐心等待</p>
          <div class="w-full bg-gray-200 rounded-full h-2.5 mb-6">
            <div 
              class="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
              :style="{ width: progress + '%' }"
            ></div>
          </div>
          <p class="text-sm text-gray-500">{{ progressText }}</p>
        </div>
      </div>

      <div v-if="currentStatus === CA_STATUS.SUCCESS" class="success-section">
        <div class="text-center p-8 bg-green-50 rounded-lg border border-green-200">
          <div class="text-6xl mb-4">✅</div>
          <h3 class="text-xl font-semibold text-green-800 mb-2">解密成功！</h3>
          <p class="text-green-600 mb-6">CA介质解密已完成，正在保存数据...</p>
          <div class="bg-green-100 border border-green-300 rounded p-4 mb-6 text-left">
            <p class="text-green-800 text-sm">
              <strong>解密信息：</strong>
            </p>
            <p class="text-green-700 text-sm mt-1">
              CA类型: {{ caInfo.catype || '104' }}
            </p>
            <p class="text-green-700 text-sm">
              解密时间: {{ decryptTime }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="currentStatus === CA_STATUS.ERROR" class="error-section">
        <div class="text-center p-8 bg-red-50 rounded-lg border border-red-200">
          <div class="text-6xl mb-4">❌</div>
          <h3 class="text-xl font-semibold text-red-800 mb-2">解密失败</h3>
          <p class="text-red-600 mb-6">{{ errorMessage }}</p>
          <div class="bg-red-100 border border-red-300 rounded p-4 mb-6 text-left">
            <p class="text-red-800 text-sm">
              <strong>可能的原因：</strong>
            </p>
            <ul class="text-red-700 text-sm mt-1 space-y-1">
              <li>• CA介质未正确插入</li>
              <li>• CA驱动未正确安装</li>
              <li>• 用户取消了PIN码输入</li>
              <li>• PIN码输入错误次数过多</li>
              <li>• CA介质已损坏或过期</li>
            </ul>
          </div>
          
          <div class="flex justify-center space-x-4">
            <a-button @click="handleBack">上一步</a-button>
            <a-button type="primary" @click="retry">
              <a-icon type="reload" class="mr-1" />重试
            </a-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { AnQingMediumCaDecrypt } from '@/api/ggb/linhai'

const ANQING_CA_TYPE = 104;
const ANQING_CA_PORT = 21345;
const ANQING_CA_SCRIPT_FLAG = 'anqingCaLoaded';
const ANQING_CA_SCRIPTS = [
  '/vendor/anqing-ca/jquery-1.11.1.min.js',
  '/vendor/anqing-ca/jquery.cookie.js',
  '/vendor/anqing-ca/iWebNet.js'
];

const CA_STATUS = {
  WAITING: 'waiting',
  DECRYPTING: 'decrypting',
  SUCCESS: 'success',
  ERROR: 'error'
};

export default {
  name: 'MediumCADecrypt',
  props: {
    toubaono: {
      type: String,
      default: ''
    },
    encryptedData: {
      type: String,
      default: ''
    },
    caType: {
      type: Number,
      default: ANQING_CA_TYPE
    },
    digitalEnvelope: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      CA_STATUS: CA_STATUS,
      currentStatus: CA_STATUS.WAITING,
      caInfo: {
        catype: this.caType,
        port: ANQING_CA_PORT,
        sn: ''
      },
      progress: 0,
      progressText: '',
      errorMessage: '',
      decryptTime: ''
    };
  },
  computed: {
    statusText() {
      const map = {
        [CA_STATUS.WAITING]: '等待插入CA介质',
        [CA_STATUS.DECRYPTING]: '正在解密...',
        [CA_STATUS.SUCCESS]: '解密成功！',
        [CA_STATUS.ERROR]: '解密失败'
      };
      return map[this.currentStatus] || '未知状态';
    },
    statusColor() {
      const map = {
        [CA_STATUS.WAITING]: 'text-blue-600',
        [CA_STATUS.DECRYPTING]: 'text-blue-600',
        [CA_STATUS.SUCCESS]: 'text-green-600',
        [CA_STATUS.ERROR]: 'text-red-600'
      };
      return map[this.currentStatus] || 'text-gray-600';
    }
  },
  methods: {
    async startDecrypt() {
      this.currentStatus = CA_STATUS.DECRYPTING;
      this.progress = 0;
      this.progressText = '正在初始化...';
      try {
        this.simulateProgress();
        await this.ensureCaControlLoaded();
        window.iWebNet.Init(ANQING_CA_TYPE);
        const epca = new window.EpCaObj(true);
        const dataToDecrypt = this.digitalEnvelope;
        if (!dataToDecrypt) {
          throw new Error('没有可解密的数据');
        }
        const decryptedKey = epca.Decrypt(dataToDecrypt);
        if (!decryptedKey) {
          throw new Error('解密失败，未获取到密钥');
        }
        this.decryptTime = new Date().toLocaleString();
        this.currentStatus = CA_STATUS.SUCCESS;
        await this.saveToBackend(decryptedKey);
      } catch (error) {
        // console.error('解密失败:', error);
        this.errorMessage = '解密过程出错: ' + this.getErrorMessage(error);
        this.currentStatus = CA_STATUS.ERROR;
      }
    },
    async saveToBackend(decryptedKey) {
      try {
        // console.log('调用后端接口保存数据...');
        const response = await AnQingMediumCaDecrypt({
          toubaono: this.toubaono,
          decryptedKey: decryptedKey,
          caMethod: 1
        });
        // console.log('后端返回结果:', response);
        setTimeout(() => {
          this.$emit('decrypt-success', {
            decryptedKey: decryptedKey,
            caMethod: 'medium',
            caInfo: this.caInfo,
            contractId: response ? response.contractId : null,
          });
        }, 1000);
      } catch (error) {
        // console.error('调用后端接口失败:', error);
        this.errorMessage = '保存数据失败: ' + this.getErrorMessage(error);
        this.currentStatus = CA_STATUS.ERROR;
      }
    },
    async ensureCaControlLoaded() {
      if (window.iWebNet && window.EpCaObj) {
        return;
      }
      for (var i = 0; i < ANQING_CA_SCRIPTS.length; i++) {
        await this.loadScript(ANQING_CA_SCRIPTS[i]);
      }
      if (!window.iWebNet || !window.EpCaObj) {
        throw new Error('未检测到CA组件，请确认已安装CA驱动');
      }
    },
    loadScript(src) {
      const scriptUrl = this.getPublicPath(src);
      const existed = document.querySelector('script[data-' + ANQING_CA_SCRIPT_FLAG + '="' + src + '"]');
      if (existed) {
        if (existed.getAttribute('data-loaded') === 'true') {
          return Promise.resolve();
        }
        return new Promise(function(resolve, reject) {
          existed.addEventListener('load', resolve, { once: true });
          existed.addEventListener('error', reject, { once: true });
        });
      }
      return new Promise(function(resolve, reject) {
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.async = false;
        script.setAttribute('data-' + ANQING_CA_SCRIPT_FLAG, src);
        script.onload = function() {
          script.setAttribute('data-loaded', 'true');
          resolve();
        };
        script.onerror = function() {
          reject(new Error('加载CA控件脚本失败: ' + src));
        };
        document.body.appendChild(script);
      });
    },
    getPublicPath(src) {
      const baseUrl = process.env.BASE_URL || '/';
      return baseUrl.replace(/\/$/, '') + src;
    },
    getErrorMessage(error) {
      if (!error) {
        return '未知错误';
      }
      if (error.description) {
        return error.description;
      }
      if (error.message) {
        return error.message;
      }
      return String(error);
    },
    simulateProgress() {
      const progressSteps = [
        { progress: 20, text: '正在初始化CA组件...' },
        { progress: 40, text: '正在检测CA介质...' },
        { progress: 60, text: '正在读取加密数据...' },
        { progress: 80, text: '正在执行解密...' },
        { progress: 100, text: '解密完成' }
      ];
      var stepIndex = 0;
      var self = this;
      const interval = setInterval(function() {
        if (stepIndex < progressSteps.length && self.currentStatus === CA_STATUS.DECRYPTING) {
          self.progress = progressSteps[stepIndex].progress;
          self.progressText = progressSteps[stepIndex].text;
          stepIndex++;
        } else {
          clearInterval(interval);
        }
      }, 400);
    },
    handleBack() {
      this.$emit('go-back');
    },
    retry() {
      this.currentStatus = CA_STATUS.WAITING;
      this.errorMessage = '';
      this.progress = 0;
      this.progressText = '';
    }
  }
};
</script>

<style scoped>
.medium-ca-decrypt-container {
  max-width: 700px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.status-section { margin-bottom: 1.5rem; }
.main-section { margin-bottom: 1.5rem; }

.waiting-section,
.processing-section,
.success-section,
.error-section {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-spin {
  animation: spin 1s linear infinite;
  display: inline-block;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
